import { useTranslation } from 'react-i18next';

import useAiChatToolLabel from '../../hooks/useAiChatToolLabel';
import useAiChatToolStatus from '../../hooks/useAiChatToolStatus';
import AiChatToolCard from '../AiChatToolCard';

import type { ReactElement } from 'react';
import type { IAiChatToolCallOf } from '../../interfaces/IAiChatToolCall';

/**
 * The `shareDocument` tool call: one of the user's private documents opened to their company.
 *
 * The call can finish without sharing anything — the output says which — so once it is done the
 * status comes from the output, not from the state. The document is named by its title when it
 * was shared; the id in the input means nothing to a reader and is not shown.
 */
function ShareDocumentToolCall({ part }: IAiChatToolCallOf<'shareDocument'>): ReactElement {
  const { t } = useTranslation();
  const label = useAiChatToolLabel('shareDocument');
  const status = useAiChatToolStatus(part.state);

  const isDone = part.state === 'output-available';
  const isShared = isDone && part.output.shared;

  const summary = isDone
    ? t(isShared ? 'aiChat.tools.documentShared' : 'aiChat.tools.documentNotShared')
    : status;

  // What was shared, or why nothing was.
  const detail = isDone && (part.output.shared ? part.output.title : part.output.reason);

  return (
    <AiChatToolCard name={label} status={summary} errorText={part.errorText}>
      {detail}
    </AiChatToolCard>
  );
}

export default ShareDocumentToolCall;
